import { Button, Tag } from '@bridebook/aura';
import { CalendarCheck } from '@bridebook/aura/src/icons';
import { useSignupRedirectProps } from '@bridebook/web/app-basic/components/supplier/hooks/use-signup-redirect-props';
import useSignAnalytics from '@bridebook/web/app-basic/lib/hooks/use-sign-analytics';
import { getSupplier } from '@bridebook/web/app-basic/lib/supplier/selectors';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { useIsAuthenticated } from '@bridebook/web/lib/auth/hooks/use-is-authenticated';
import {
  EVENT_ALL_PARAM,
  SIGNUP_FLOW_PARAM,
} from '@bridebook/web/lib/showrounds/constants/showround';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Code-split the signup modal out of the basic page bundle; it only loads when a
// logged-out user opens it (which also defers the reCAPTCHA enterprise script).
const SignupModal = dynamic(() => import('@bridebook/web/components/auth/signup-modal'), {
  ssr: false,
});

export const BookAViewingButton = () => {
  const { t } = useTranslation('supplier');
  const supplier = useSelector(getSupplier);
  const { isAuthenticated, isLoading } = useIsAuthenticated();
  const [showSignupModal, setShowSignupModal] = useState(false);
  const router = useRouter();

  const analyticsHandler = useSignAnalytics('supplierProfile', 'enquiryDetails', 'Book a viewing');

  // Paying venues skip onboarding, return to the profile, and open showrounds via
  // `?event=all`. Limited profiles keep onboarding and land on a venue search afterwards.
  // The `fromSignup` marker tells the profile that the modal opened off the back of this
  // CTA, so it can be tracked separately from supplier shared availability links.
  const signupLinkProps = useSignupRedirectProps({
    searchAfterOnboarding: true,
    profilePageQuery: { event: EVENT_ALL_PARAM, [SIGNUP_FLOW_PARAM]: 'true' },
  });

  const handleClick = useCallback(() => {
    if (isLoading) return;

    if (isAuthenticated) {
      // Authenticated users normally never see the basic page (middleware routes them
      // to the logged-in profile); navigate there with the showround deep-link.
      void router.push(
        `${router.asPath.split('?')[0]}?event=${EVENT_ALL_PARAM}&${SIGNUP_FLOW_PARAM}=true`,
      );
      return;
    }

    analyticsHandler();
    setShowSignupModal(true);
  }, [analyticsHandler, isAuthenticated, isLoading, router]);

  const handleCloseModal = useCallback(() => {
    setShowSignupModal(false);
  }, []);

  if (!supplier) return null;

  return (
    <div className="relative w-full">
      <Tag theme="teal80" size="small" className="absolute -top-2 right-3 z-10">
        {t('supplierContactButtons.popular')}
      </Tag>
      <Button
        icon={CalendarCheck}
        variant="secondary"
        size="standard"
        className="w-full"
        onClick={handleClick}>
        {t('bookAViewing.ctaButton')}
      </Button>
      <SignupModal
        isOpen={showSignupModal}
        onClose={handleCloseModal}
        signupLinkProps={signupLinkProps}
        title={t('bookAViewing.signupModal.title', {
          name: supplier.name,
        })}
        descriptions={[
          t('bookAViewing.signupModal.bulletpoint1'),
          t('bookAViewing.signupModal.bulletpoint2'),
          t('bookAViewing.signupModal.bulletpoint3'),
        ]}
      />
    </div>
  );
};
